'use client';

import { createContext, type ReactNode, use, useCallback, useEffect, useMemo, useState } from 'react';
import { noop } from '@interxfi/utilities/fn/no-op';
import { getCSSVariableValue } from './util/style';

const DARK_MODE_MEDIA_MATCH_STRING = '(prefers-color-scheme: dark)';

type Theme = 'UNSPECIFIED' | 'DARK' | 'LIGHT' | 'SYSTEM';

export type RenderedTheme = Exclude<Theme, 'UNSPECIFIED' | 'SYSTEM'>;

const THEME_META_ID = 'ixf-theme-meta';

// This should address the iOS overscroll background color
function updateThemeMeta() {
  if (typeof document !== 'undefined') {
    let meta: HTMLElement | null = document.getElementById(THEME_META_ID);
    const backgroundColor = getCSSVariableValue('--ixf-color-background');

    if (backgroundColor) {
      if (!meta) {
        const metaElement = document.createElement('meta');
        metaElement.id = THEME_META_ID;
        metaElement.name = 'theme-color';
        document.head.appendChild(metaElement);
        meta = metaElement;
      }

      meta?.setAttribute('content', backgroundColor);
    }
  }
}

export function useSystemThemePreference(skip?: boolean, onChange?: (theme: Theme) => void) {
  const isWindowDefined = typeof window !== 'undefined';
  const [theme, setTheme] = useState<RenderedTheme>(() =>
    isWindowDefined && window.matchMedia && window.matchMedia(DARK_MODE_MEDIA_MATCH_STRING).matches ? 'DARK' : 'LIGHT',
  );

  useEffect(() => {
    const match = isWindowDefined && window.matchMedia ? window.matchMedia(DARK_MODE_MEDIA_MATCH_STRING) : null;
    const handleMatchChange = (e: MediaQueryListEvent) => {
      const nextValue = e.matches ? 'DARK' : 'LIGHT';

      setTheme(nextValue);
      onChange?.(nextValue);
      updateThemeMeta();
    };

    if (match && !skip) {
      match.addEventListener('change', handleMatchChange);
    }

    return () => {
      if (match) {
        match.removeEventListener('change', handleMatchChange);
      }
    };
  }, [skip, isWindowDefined, onChange]);

  return theme;
}

const PREFERRED_THEME_LOCAL_STORAGE_KEY = 'ixf_preferred_theme';

export function getPreferredThemeFromLocalStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(PREFERRED_THEME_LOCAL_STORAGE_KEY) as RenderedTheme | null;
}

export function setPreferredThemeInLocalStorage(theme: Theme) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(PREFERRED_THEME_LOCAL_STORAGE_KEY, theme);
}

export const ThemeContext = createContext<{ theme: Theme; setTheme: (theme: Theme) => void }>({
  theme: getPreferredThemeFromLocalStorage() || 'LIGHT',
  setTheme: noop,
});

interface ThemeProviderProps {
  children: ReactNode;
  userThemePreference?: Theme;
}

export function ThemeProvider({ children, userThemePreference }: ThemeProviderProps) {
  const [preferredTheme, setPreferredTheme] = useState<Theme>(userThemePreference || getPreferredThemeFromLocalStorage() || 'LIGHT');
  const systemThemePreference = useSystemThemePreference(preferredTheme !== 'SYSTEM');
  const renderedTheme = preferredTheme === 'SYSTEM' ? systemThemePreference : preferredTheme;
  const handleSetThemePreference = useCallback((newTheme: Theme) => {
    setPreferredThemeInLocalStorage(newTheme);
    setPreferredTheme(newTheme);
  }, []);

  useEffect(() => {
    if (userThemePreference) {
      handleSetThemePreference(userThemePreference);
    }
  }, [userThemePreference, handleSetThemePreference]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.dataset.theme = renderedTheme;
      updateThemeMeta();
    }
  }, [renderedTheme]);

  const value = useMemo(
    () => ({
      theme: renderedTheme,
      setTheme: handleSetThemePreference,
    }),
    [renderedTheme, handleSetThemePreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return use(ThemeContext);
}
